import './polaroid-image.scss';

import type { StaticImageData } from 'next/image';

import { Image } from '@sola/ark-core/atoms/image/image.tsx';
import {
  ChildImageVariantEnum,
  getChildImageUrl,
} from '@sola/ark-core/utils/child-image.ts';
import { cn } from '@sola/ark-core/utils/helpers.ts';

export type PolaroidImageSize = 'sm' | 'lg';
export type PolaroidImageTilt = 'left' | 'right' | 'randomized' | 'none';

type WithChildImage = {
  /**
   * Child image URL from the Compassion CDN (must contain the `/w_150/` Cloudinary template).
   * If the URL doesn't match the expected format, the raw URL is used as a fallback.
   */
  childImageUrl: string;
  imgSrc?: never;
};

type WithImgSrc = {
  /** Static image data or pre-built URL */
  imgSrc: string | StaticImageData;
  childImageUrl?: never;
};

const sizeMap = {
  sm: { width: 73, height: 88 },
  lg: { width: 100, height: 121 },
};

type PolaroidImageBase = {
  alt: string;
  size?: PolaroidImageSize;
  tilt?: PolaroidImageTilt;
};

const getTiltFromUrl = (url: string): 'left' | 'right' => {
  let total = 0;
  for (let i = 0; i < url.length; i += 1) {
    total += url.codePointAt(i) ?? 0;
  }
  return total % 2 === 0 ? 'left' : 'right';
};

export type PolaroidImageProps = PolaroidImageBase &
  (WithChildImage | WithImgSrc);

export const PolaroidImage: React.FC<PolaroidImageProps> = ({
  childImageUrl,
  imgSrc,
  alt,
  size = 'lg',
  tilt = 'none',
}) => {
  const { width, height } = sizeMap[size];

  const src = childImageUrl
    ? (getChildImageUrl(
        childImageUrl,
        ChildImageVariantEnum.Portrait,
        width * 2,
      ) ?? childImageUrl)
    : imgSrc;

  if (!src) {
    return null;
  }

  let resolvedTilt: PolaroidImageTilt = tilt;
  if (tilt === 'randomized') {
    const url = typeof src === 'string' ? src : src.src;
    resolvedTilt = getTiltFromUrl(url);
  }

  return (
    <div
      className={cn('PolaroidImage', {
        'PolaroidImage--sm': size === 'sm',
        'PolaroidImage--tilt-left': resolvedTilt === 'left',
        'PolaroidImage--tilt-right': resolvedTilt === 'right',
      })}
    >
      <div className="PolaroidImage__frame">
        <Image
          src={src}
          alt={alt}
          width={width}
          height={height}
          className="PolaroidImage__image"
        />
      </div>
    </div>
  );
};
